<template>
    <div class="min-h-screen bg-white">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div class="text-center mb-2.5">
                <h1 class="font-header text-[3rem] md:text-[3rem] font-bold text-primary mb-1.5">
                    Contact Us
                </h1>
                <div v-if="pageData?.description"
                    class="font-body text-[1.125rem] text-gray-700 max-w-4xl mx-auto leading-[1.75] content-page-description"
                    v-html="pageData.description">
                </div>
            </div>

            <div v-if="pageData?.sections && pageData.sections.length > 0"
                class="max-w-4xl mx-auto space-y-[0.196rem] mb-2.5">
                <div v-for="(section, index) in pageData.sections" :key="index"
                    class="bg-white rounded-lg shadow-sm p-2.5 md:p-4">
                    <h2 v-if="section.title"
                        class="font-header text-[1.5rem] md:text-[1.5rem] font-bold text-primary mb-1">
                        {{ section.title }}
                    </h2>
                    <div class="font-body text-[1.125rem] text-gray-700 leading-[1.75] content-page-section"
                        v-html="section.content"></div>
                </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-1.5 max-w-6xl mx-auto">
                <div class="bg-white rounded-lg shadow-sm p-2.5 md:p-4">
                    <h2 class="font-header text-[1.5rem] md:text-[1.5rem] font-bold text-primary mb-1.5">
                        Get in Touch
                    </h2>
                    <form @submit.prevent="handleSubmit" class="space-y-1.5">
                        <div>
                            <label for="name" class="block text-sm font-header font-semibold text-primary mb-2.5">
                                Name <span class="text-red-500">*</span>
                            </label>
                            <input type="text" id="name" v-model="form.name" required
                                class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                                placeholder="Your name" />
                        </div>
                        <div>
                            <label for="email" class="block text-sm font-header font-semibold text-primary mb-2.5">
                                Email <span class="text-red-500">*</span>
                            </label>
                            <input type="email" id="email" v-model="form.email" required
                                class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                                placeholder="your.email@example.com" />
                        </div>
                        <div>
                            <label for="subject" class="block text-sm font-header font-semibold text-primary mb-2.5">
                                Subject <span class="text-red-500">*</span>
                            </label>
                            <input type="text" id="subject" v-model="form.subject" required
                                class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                                placeholder="What is this regarding?" />
                        </div>
                        <div>
                            <label for="message" class="block text-sm font-header font-semibold text-primary mb-2.5">
                                Message <span class="text-red-500">*</span>
                            </label>
                            <textarea id="message" v-model="form.message" required rows="6"
                                class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75] resize-none"
                                placeholder="Tell us more..."></textarea>
                        </div>
                        <button type="submit" :disabled="isSubmitting"
                            class="w-full bg-accent text-white py-3 px-6 rounded-md font-header font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            {{ isSubmitting ? 'Sending...' : 'Send Message' }}
                        </button>
                        <p v-if="submitMessage"
                            :class="submitMessage.includes('success') ? 'text-green-600' : 'text-red-600'"
                            class="font-body text-[1.125rem] text-center leading-[1.75]">
                            {{ submitMessage }}
                        </p>
                    </form>
                </div>

                <div class="space-y-1.5">
                    <div v-if="contactFields.length > 0" class="bg-white rounded-lg shadow-sm p-2.5 md:p-4">
                        <h2 class="font-header text-[1.5rem] md:text-[1.5rem] font-bold text-primary mb-1.5">
                            Contact Information
                        </h2>
                        <div class="space-y-1.5">
                            <div v-for="(field, index) in contactFields" :key="index" class="flex items-start">
                                <div class="flex-shrink-0">
                                    <svg v-if="field.type === 'email'" class="w-6 h-6 text-accent mt-1" fill="none"
                                        stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    <svg v-else-if="field.type === 'phone'" class="w-6 h-6 text-accent mt-1" fill="none"
                                        stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                    <svg v-else-if="field.type === 'address'" class="w-6 h-6 text-accent mt-1"
                                        fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                </div>
                                <div class="ml-4">
                                    <h3 class="font-header text-[1.125rem] font-semibold text-primary mb-1.5">{{
                                        field.label }}</h3>
                                    <a v-if="field.type === 'email'" :href="`mailto:${field.value}`"
                                        class="font-body text-[1.125rem] text-gray-700 leading-[1.75] hover:text-accent transition-colors">
                                        {{ field.value }}
                                    </a>
                                    <a v-else-if="field.type === 'phone'" :href="`tel:${field.value}`"
                                        class="font-body text-[1.125rem] text-gray-700 leading-[1.75] hover:text-accent transition-colors">
                                        {{ field.value }}
                                    </a>
                                    <p v-else
                                        class="font-body text-[1.125rem] text-gray-700 leading-[1.75] whitespace-pre-line">
                                        {{ field.value }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { contactService } from '../services/contact'
import { publicService } from '../services/public'

const form = ref({
    name: '',
    email: '',
    subject: '',
    message: ''
})

const isSubmitting = ref(false)
const submitMessage = ref('')
const contactFields = ref([])
const pageData = ref(null)

const loadContactInfo = async () => {
    try {
        const result = await contactService.getContactInfo()
        if (result.success && result.data?.fields) {
            contactFields.value = result.data.fields
        }
    } catch (error) {
        console.error('Failed to load contact information:', error)
    }
}

const loadPageData = async () => {
    try {
        const data = await publicService.getPage('Contact Us')
        pageData.value = data
    } catch (error) {
        console.error('Failed to load Contact page:', error)
    }
}

const handleSubmit = async () => {
    isSubmitting.value = true
    submitMessage.value = ''

    try {

        const result = await contactService.submitContactForm(form.value)
        if (result.success) {
            submitMessage.value = 'Thank you for your message! We will get back to you soon.'
            form.value = {
                name: '',
                email: '',
                subject: '',
                message: ''
            }
        } else {
            submitMessage.value = result.error || 'Sorry, there was an error sending your message. Please try again.'
        }
    } catch (error) {
        submitMessage.value = 'Sorry, there was an error sending your message. Please try again.'
    } finally {
        isSubmitting.value = false
    }
}

onMounted(() => {
    loadContactInfo()
    loadPageData()
})
</script>

<style scoped></style>
